import { computed, ref } from "vue";
import type { ComputedRef, Ref } from "vue";

import type { Controller, WorkspaceId } from "@ctrl/controller-rpc";

import {
  brokenProvisionedWorkspaces,
  ensureProvisionedDatabases,
} from "@/components/workspaces";
import type { UseProfile } from "@/composables/useProfile";

/**
 * Screen-level guard of the provisioned types (Task, Page, Project) of an
 * organization, shared by the Tasks/Pages/Projects screens.
 *
 * On creation it inspects all three types of every workspace of the
 * organization (see `brokenProvisionedWorkspaces`); when any is broken, the
 * screen replaces its whole content with a Repair empty state — even when
 * the displayed workspace or the screen's own type is unaffected, since the
 * types are interdependent (e.g. the Task type's "Project" relation). The
 * repair then heals all the broken workspaces at once, and re-inspects.
 *
 * Creation paths never provision: this explicit Repair action is the only
 * user-facing path to `ensureProvisionedDatabases` (besides workspace
 * initialization).
 *
 * Owns no resources (plain refs, no watchers): safe to call from any setup,
 * before or after an `await`.
 */
export const useProvisionedRepair = (
  controller: Controller,
  /** Identifies the personal workspace, whose Task and Page types are the
   * hardcoded built-in tables: only its Project side is inspected and
   * repaired. */
  profile: Ref<UseProfile>,
  workspaceIds: () => WorkspaceId[],
  /** Screen-specific refresh, run after a successful repair (e.g. reload
   * the task sections, or watch the restored Page rows). */
  afterRepair?: () => Promise<void> | void
): {
  /** Whether any workspace is broken and the screen should show the Repair
   * empty state. False while the initial inspection is still resolving. */
  needsRepair: ComputedRef<boolean>;
  isRepairing: Ref<boolean>;
  /** Whether the last repair attempt left something broken (a write failed,
   * or the re-inspection still reports broken workspaces): the empty state
   * then also explains how to fix the types manually. Reset on the next
   * attempt. */
  repairFailed: Ref<boolean>;
  repair: () => Promise<void>;
} => {
  const brokenWorkspaces = ref<WorkspaceId[]>([]);
  const needsRepair = computed(() => brokenWorkspaces.value.length > 0);
  const isRepairing = ref(false);
  const repairFailed = ref(false);

  const inspect = async (): Promise<void> => {
    try {
      brokenWorkspaces.value = await brokenProvisionedWorkspaces(
        controller,
        profile.value,
        workspaceIds()
      );
    } catch (error) {
      // Fail open (offline, transient read failure): the screens render
      // normally, as if everything were intact.
      brokenWorkspaces.value = [];
      window.console.error("Could not inspect the provisioned types:", error);
    }
  };

  const repair = async (): Promise<void> => {
    if (isRepairing.value) return;
    isRepairing.value = true;
    repairFailed.value = false;
    try {
      await Promise.all(
        brokenWorkspaces.value.map((workspaceId) =>
          ensureProvisionedDatabases(controller, profile.value, workspaceId)
        )
      );
      await afterRepair?.();
    } catch (error) {
      repairFailed.value = true;
      window.console.error("Could not repair the provisioned types:", error);
    } finally {
      await inspect();
      isRepairing.value = false;
      if (needsRepair.value) repairFailed.value = true;
    }
  };

  void inspect();

  return { needsRepair, isRepairing, repairFailed, repair };
};
